import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import ClearTeamAssistAnswer from 'actions/team_assist/clear_team_assist_answer';
import FetchTeamAssistAnswer from 'actions/team_assist/fetch_team_assist_answer';
import RetryTeamAssist from 'actions/team_assist/retry_team_assist';
import UseTeamAssistResponse from 'actions/team_assist/use_team_assist_response';
import SafeHtml from 'components/common/safe_html';
import TeamAssistAnswerPreviewModal from 'components/customer/team_assist/team_assist_answer_preview_modal';
import { TeamAssistLoadingDots } from 'components/customer/team_assist/team_assist_shared';
import { useExecuteAction } from 'components/hooks/connect_hooks';
import TeamAssistMessage from 'models/team_assist_message';

function getPlainTextFromHtml(html) {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
}

function replaceCitationRefsInContent(html, citations) {
  if (!html || !citations || citations.length === 0) return html;

  let result = html;
  citations.forEach((citation, idx) => {
    const num = idx + 1;
    const escapedId = citation.snippetId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const sup = `<sup class="cite-ref">[${num}]</sup>`;
    // Match patterns like (snippetId – Name) or (snippetId - Name), optionally wrapped in <em>/<i> tags
    const pattern = new RegExp(`(?:<(?:em|i)>)?\\(?${escapedId}(?:\\s*[–—-]\\s*[^)<]*)?\\)?(?:</(?:em|i)>)?`, 'g');
    result = result.replace(pattern, sup);
  });
  return result;
}

function MessageBubble({ msg, onUse }) {
  const executeAction = useExecuteAction();
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [previewAnswerId, setPreviewAnswerId] = useState(null);
  const hasCitations = msg.citations && msg.citations.length > 0;
  const displayContent = hasCitations ? replaceCitationRefsInContent(msg.content, msg.citations) : msg.content;

  const handleCopy = (content, messageId) => {
    const plainText = getPlainTextFromHtml(content);
    const html = content || '';
    if (!plainText && !html.trim()) return;

    const writeClipboard = () => {
      const item = {
        'text/plain': new Blob([plainText], { type: 'text/plain' }),
        'text/html': new Blob([html], { type: 'text/html' }),
      };
      return navigator.clipboard.write([new window.ClipboardItem(item)]);
    };

    writeClipboard()
      .then(() => {
        setCopiedMessageId(messageId);
        setTimeout(() => setCopiedMessageId(null), 2000);
      })
      .catch(() => {
        navigator.clipboard.writeText(plainText).then(() => {
          setCopiedMessageId(messageId);
          setTimeout(() => setCopiedMessageId(null), 2000);
        });
      });
  };

  const hasReviewReasons = msg.reviewReasons && msg.reviewReasons.length > 0;

  const renderReviewReasons = () => {
    if (!hasReviewReasons) {
      return null;
    }
    return (
      <ReviewReasonsContainer>
        <ReviewReasonsIcon className="fa fa-exclamation-triangle" />
        <ReviewReasonsText>
          <ReviewReasonsLabel>Needs review</ReviewReasonsLabel>
          {msg.reviewReasons.map((reason, idx) => (
            <ReviewReason key={idx}>{reason}</ReviewReason>
          ))}
        </ReviewReasonsText>
      </ReviewReasonsContainer>
    );
  };

  const renderCitations = () => {
    if (!hasCitations) {
      return null;
    }
    return (
      <>
        <CitationsDivider />
        <CitationsLabel>Answers referenced</CitationsLabel>
        <CitationsList>
          {msg.citations.map((citation, idx) => (
            <CitationLink
              href="#"
              key={citation.snippetId}
              onClick={evt => {
                evt.preventDefault();
                setPreviewAnswerId(citation.snippetId);
                executeAction(FetchTeamAssistAnswer, { answerId: citation.snippetId });
              }}
            >
              <CitationNumber>[{idx + 1}]</CitationNumber> {citation.snippetName || citation.snippetId}
            </CitationLink>
          ))}
        </CitationsList>
        {previewAnswerId && (
          <TeamAssistAnswerPreviewModal
            onClose={() => {
              setPreviewAnswerId(null);
              executeAction(ClearTeamAssistAnswer);
            }}
          />
        )}
      </>
    );
  };

  if (msg.role === TeamAssistMessage.Role.agent_question) {
    return (
      <UserRow>
        <UserBubbleSpacer />
        <UserBubble>
          <SafeHtml html={msg.content || ''} />
        </UserBubble>
      </UserRow>
    );
  }

  const isCopied = copiedMessageId === msg.id;
  const copyButton = (
    <CopyButton
      className="team-assist-copy-button"
      data-aid="teamAssistCopyMessageButton"
      isCopied={isCopied}
      onClick={() => handleCopy(msg.content, msg.id)}
      title="Copy"
      type="button"
    >
      <i className={isCopied ? 'fa fa-check' : 'fa fa-copy'} />
    </CopyButton>
  );

  if (msg.type === TeamAssistMessage.Type.suggested_response) {
    return (
      <AssistantRow>
        <AssistantMessageWrapper>
          <AssistantBubble>
            {msg.isRetrying ? (
              <TeamAssistLoadingDots />
            ) : (
              <>
                <SuggestedResponseContent>
                  <SafeHtml html={displayContent || ''} />
                </SuggestedResponseContent>
                {renderReviewReasons()}
                <SuggestedResponseActions>
                  <UseButton
                    onClick={() => {
                      executeAction(UseTeamAssistResponse, {
                        customerId: msg.customerId,
                        messageId: msg.id,
                      });
                      onUse?.();
                    }}
                    type="button"
                  >
                    Use
                  </UseButton>
                  <RetryButton
                    onClick={() =>
                      executeAction(RetryTeamAssist, {
                        customerId: msg.customerId,
                        messageId: msg.id,
                      })
                    }
                    type="button"
                  >
                    Retry
                  </RetryButton>
                </SuggestedResponseActions>
                {renderCitations()}
              </>
            )}
          </AssistantBubble>
          {copyButton}
        </AssistantMessageWrapper>
      </AssistantRow>
    );
  }

  return (
    <AssistantRow>
      <AssistantMessageWrapper>
        <AssistantBubble>
          <SuggestedResponseContent>
            <SafeHtml html={displayContent || ''} />
          </SuggestedResponseContent>
          {renderReviewReasons()}
          {renderCitations()}
        </AssistantBubble>
        {copyButton}
      </AssistantMessageWrapper>
    </AssistantRow>
  );
}

MessageBubble.propTypes = {
  msg: PropTypes.shape({
    citations: PropTypes.arrayOf(
      PropTypes.shape({
        snippetId: PropTypes.string,
        snippetName: PropTypes.string,
      })
    ),
    confidence: PropTypes.number,
    content: PropTypes.string,
    customerId: PropTypes.string,
    id: PropTypes.string,
    isRetrying: PropTypes.bool,
    reviewReasons: PropTypes.arrayOf(PropTypes.string),
    role: PropTypes.string,
    type: PropTypes.string,
  }),
  onUse: PropTypes.func,
};

export default MessageBubble;
export { AssistantRow };

const UserBubbleSpacer = styled.div`
  flex-shrink: 0;
  width: 22px;
`;

const UserRow = styled.div`
  align-self: flex-end;
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
  max-width: 100%;
  width: 100%;
`;

const AssistantRow = styled.div`
  align-self: flex-start;
  display: flex;
  justify-content: flex-start;
  margin-bottom: 12px;
  max-width: 100%;
  width: fit-content;
`;

const AssistantMessageWrapper = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 8px;
  max-width: 100%;

  &:hover .team-assist-copy-button {
    opacity: 1;
  }
`;

const CopyButton = styled.button`
  background: none;
  border: none;
  color: ${p => p.theme.colors.gray500};
  cursor: pointer;
  flex-shrink: 0;
  font-size: 14px;
  opacity: ${p => (p.isCopied ? 1 : 0)};
  padding: 4px;
  transition: opacity 0.15s ease;

  &:hover {
    color: ${p => p.theme.colors.gray700};
  }
`;

const UserBubble = styled.div`
  background: ${p => p.theme.colors.purple400};
  border-radius: 12px 2px 12px 12px;
  color: ${p => p.theme.colors.white};
  font-size: ${p => p.theme.fontSize.base};
  line-height: 1.4;
  max-width: 100%;
  padding: 8px 12px;
  word-wrap: break-word;
`;

const AssistantBubble = styled.div`
  background: ${p => p.theme.colors.gray100};
  border-radius: 2px 12px 12px 12px;
  color: ${p => p.theme.colors.black};
  font-size: ${p => p.theme.fontSize.base};
  line-height: 1.4;
  max-width: 100%;
  padding: 8px 12px;
  word-wrap: break-word;
`;

const SuggestedResponseContent = styled.div`
  p {
    margin: 0 0 8px 0;
    &:last-child {
      margin-bottom: 0;
    }
  }
  ul,
  ol {
    margin: 4px 0;
    padding-left: 20px;
  }
  sup.cite-ref {
    color: ${p => p.theme.colors.purple400};
    font-size: 10px;
    font-weight: 600;
  }
`;

const SuggestedResponseActions = styled.div`
  border-top: 1px solid ${p => p.theme.colors.gray300};
  display: flex;
  gap: 8px;
  margin-top: 8px;
  padding-top: 8px;
`;

const UseButton = styled.button`
  background: ${p => p.theme.colors.purple400};
  border: none;
  border-radius: 4px;
  color: ${p => p.theme.colors.white};
  cursor: pointer;
  font-size: 12px;
  padding: 4px 12px;
  &:hover {
    background: ${p => p.theme.colors.purple500};
  }
`;

const RetryButton = styled.button`
  background: transparent;
  border: 1px solid ${p => p.theme.colors.gray300};
  border-radius: 4px;
  color: ${p => p.theme.colors.gray600};
  cursor: pointer;
  font-size: 12px;
  padding: 4px 12px;
  &:hover {
    background: ${p => p.theme.colors.gray200};
  }
`;

const ReviewReasonsContainer = styled.div`
  align-items: flex-start;
  background: ${p => p.theme.colors.yellow100};
  border-radius: 4px;
  display: flex;
  gap: 6px;
  margin-top: 8px;
  padding: 6px 8px;
`;

const ReviewReasonsIcon = styled.i`
  color: ${p => p.theme.colors.yellow600};
  flex-shrink: 0;
  font-size: 12px;
  margin-top: 2px;
`;

const ReviewReasonsText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ReviewReasonsLabel = styled.span`
  color: ${p => p.theme.colors.yellow700};
  font-size: 11px;
  font-weight: 600;
`;

const ReviewReason = styled.span`
  color: ${p => p.theme.colors.yellow600};
  font-size: 11px;
  line-height: 1.3;
`;

const CitationsDivider = styled.hr`
  border: none;
  border-top: 1px solid ${p => p.theme.colors.gray300};
  margin: 12px 0 8px 0;
`;

const CitationsLabel = styled.span`
  color: ${p => p.theme.colors.gray600};
  font-size: 12px;
  margin-bottom: 4px;
`;

const CitationsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  font-size: 12px;
  gap: 4px 12px;
  line-height: 1.3;
`;

const CitationNumber = styled.span`
  font-weight: 600;
`;

const CitationLink = styled.a`
  color: ${p => p.theme.colors.purple400};
  text-decoration: none;
  &:hover {
    color: ${p => p.theme.colors.purple500};
  }
`;
